import { useState, type FormEvent } from 'react'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { Wordmark } from '@/components/Wordmark'
import { APP_NAME } from '@/config'
import { findHouseholdByEmail, joinHousehold } from '@/data/household'
import { clearPendingInvite, pendingInvite, startHousehold } from '@/data/invites'
import { signOutUser, useAuth } from '@/store/auth'
import { firstName, setOnboarding, useHousehold } from '@/store/household'
import './Onboarding.css'

function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * A signed-in person in no household. Cuplzap is invite-only: an invite code starts a household, and
 * someone joining a household is let in by the address its owner typed.
 */
export function WelcomeScreen() {
  const user = useAuth((s) => s.user)!
  const storeError = useHousehold((s) => s.error)
  const [code, setCode] = useState(pendingInvite)
  const [name, setName] = useState(`${firstName(user.displayName) || 'Our'} household`)
  const [otherEmail, setOtherEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checked, setChecked] = useState<'idle' | 'none'>('idle')

  const onStart = async (e: FormEvent) => {
    e.preventDefault()
    if (!code.trim()) {
      setError('Enter the invite code you were sent.')
      return
    }
    if (!name.trim()) {
      setError('Give the household a name.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const displayName = firstName(user.displayName) || 'Me'
      const hid = await startHousehold({ code, name, otherEmail, timeZone: deviceTimeZone(), displayName })
      // the function made the household; the user document is ours to write, as for anyone joining
      await joinHousehold(hid, user.uid, displayName)
      clearPendingInvite()
      setOnboarding('categories')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const onCheck = async () => {
    setBusy(true)
    try {
      const hid = await findHouseholdByEmail(user.email)
      if (hid) {
        // The household listener picks this up on the next user-document change;
        // reloading is the simplest way to re-run the lookup.
        window.location.reload()
        return
      }
      setChecked('none')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="onboard">
      <header className="onboard__header">
        <Wordmark className="onboard__logo" />
        <h1 className="onboard__title">Start your household</h1>
        <p className="onboard__lead">{APP_NAME} is invite-only. Enter the code you were sent to start a household for the two of you.</p>
      </header>

      <form className="onboard__form" onSubmit={onStart}>
        <Field
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="K7M-4QX"
          maxLength={80}
        />
        <Field label="Household name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" maxLength={60} required />
        <Field
          label="The other person's Google email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={otherEmail}
          onChange={(e) => setOtherEmail(e.target.value)}
          placeholder="name@gmail.com"
          hint="They join when they sign in with this address. You can add it later in Settings."
        />
        {(error || storeError) && (
          <p className="onboard__error" role="alert">
            {error || storeError}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>
          Start household
        </Button>
      </form>

      <section className="onboard__alt">
        <h2 className="onboard__subtitle">Joining someone's household?</h2>
        <p className="onboard__lead">
          Ask them to add <strong>{user.email}</strong> in their Settings, then tap Check again.
        </p>
        <div className="onboard__buttons">
          <Button variant="secondary" onClick={onCheck} disabled={busy}>
            Check again
          </Button>
          <Button variant="quiet" onClick={() => void signOutUser()}>
            Sign out
          </Button>
        </div>
        {checked === 'none' && <p className="onboard__note">No household lists {user.email} yet.</p>}
      </section>
    </main>
  )
}
